import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import type { EditorScreen, ScreenContentCard } from './types/screen';
import { QUIZ_SECTION_LABEL_MAX } from '@/components/quiz-runtime/quizHeader';
import { readScreenContentCard } from '@/player/screenContentCard';
import {
  QUIZ_CTA_PLACEMENTS,
  type QuizCtaPlacement,
} from '@/components/quiz-runtime/quizCta';

/**
 * Поля экрана для шапки и нижней кнопки повтора эталона Slimkit (пачка
 * настроек 2, spec shared/header.md и shared/cta-button.md «Что добавить»):
 * «начало раздела», «скрыть шапку», «скрыть полосу и счётчик»
 * (stylepack/hide-progress), «скрыть „назад“ в шапке»
 * (stylepack/header-back-total), «скрыть лого», подложка нижней кнопки,
 * подпись раздела (шаги-разделы шапки, слово владельца 21.10.2026).
 *
 * Хранится только ЗАДАННОЕ: снятая галочка и «как в стиле» физически удаляют
 * ключ — экран без этих полей выглядит ровно как до пачки, а в базе нет
 * мусорных `false`.
 */
export type ScreenCtaSurface = NonNullable<EditorScreen['ctaSurface']>;

export const SCREEN_CTA_SURFACES: readonly ScreenCtaSurface[] = [
  'none',
  'solid',
  'solid-when-scroll',
  'fog',
];

export interface ScreenChromeFlagsPatch {
  progressSection?: boolean;
  hideChrome?: boolean;
  hideProgress?: boolean;
  hideHeaderBack?: boolean;
  /** Не считать экран в счётчике шагов шапки (quizShownStep.ts). */
  progressSkip?: boolean;
  hideLogo?: boolean;
  /** null/undefined — «как в стиле» (ключ удаляется). */
  ctaSurface?: ScreenCtaSurface | null;
  /**
   * Подпись раздела — как набрана (пробел в конце при наборе не съедается),
   * не длиннее QUIZ_SECTION_LABEL_MAX; пусто или null — ключ удаляется.
   */
  progressSectionLabel?: string | null;
  /** Карточка вокруг содержимого (stylepack/content-card); null — снять. */
  contentCard?: ScreenContentCard | null;
  /** Положение главной кнопки; null/undefined — «как у воронки» (ключ удаляется). */
  ctaPlacement?: QuizCtaPlacement | null;
}

type FlagKey =
  | 'progressSection'
  | 'hideChrome'
  | 'hideProgress'
  | 'progressSkip'
  | 'hideHeaderBack'
  | 'hideLogo';
const FLAG_KEYS: readonly FlagKey[] = [
  'progressSection',
  'hideChrome',
  'hideProgress',
  'hideHeaderBack',
  'progressSkip',
  'hideLogo',
];

export function applyScreenChromeFlags(
  screen: EditorScreen,
  patch: ScreenChromeFlagsPatch,
): EditorScreen {
  const next: EditorScreen = { ...screen };
  for (const key of FLAG_KEYS) {
    if (!(key in patch)) continue;
    if (patch[key] === true) next[key] = true;
    else delete next[key];
  }
  if ('ctaSurface' in patch) {
    const value = patch.ctaSurface;
    if (value && SCREEN_CTA_SURFACES.includes(value)) next.ctaSurface = value;
    else delete next.ctaSurface;
  }
  if ('progressSectionLabel' in patch) {
    const value = patch.progressSectionLabel;
    if (typeof value === 'string' && value !== '') {
      next.progressSectionLabel = value.slice(0, QUIZ_SECTION_LABEL_MAX);
    } else delete next.progressSectionLabel;
  }
  if ('contentCard' in patch) {
    const card = patch.contentCard
      ? readScreenContentCard(patch.contentCard)
      : undefined;
    if (card) next.contentCard = card;
    else delete next.contentCard;
  }
  if ('ctaPlacement' in patch) {
    const value = patch.ctaPlacement;
    if (value && QUIZ_CTA_PLACEMENTS.includes(value)) next.ctaPlacement = value;
    else delete next.ctaPlacement;
  }
  return next;
}

/**
 * Тело действия `setScreenChromeFlags` (имя — `editor/setScreenChromeFlags`).
 * Живёт здесь, а не в editorSlice.ts: фасад держит храповик длины (WEB-2552,
 * FACADE_MAX_LINES в store/__tests__/editorSlice.test.ts), и новые действия
 * экрана приходят в него одной строкой `withHistoryPayload(<тело отсюда>)`.
 */
export function setScreenChromeFlagsReducer(
  state: EditorState,
  action: PayloadAction<{ screenId: string; patch: ScreenChromeFlagsPatch }>,
): EditorState {
  const { screenId, patch } = action.payload;
  return {
    ...state,
    content: state.content.map((s) =>
      s.id === screenId ? applyScreenChromeFlags(s, patch) : s,
    ),
    isDirty: true,
  };
}
